import { Grid } from '@material-ui/core';
import { Form } from '@unform/web';
import React, { useCallback, useEffect, useState } from 'react';
import Select from '../../../../../../components/Select';
import { useToast } from '../../../../../../hooks/toast';
import {
  ButtonStyled,
  InformativeSpan,
  PageTitle,
  Wrapper,
} from '../../styles';
import api from '../../../../../../services/api';
import { useAuth } from '../../../../../../hooks/auth';

const SuggestiveProduct: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();

  const [produtos, setProdutos] = useState<any[]>([]);
  const [selectedProduto, setSelectedProduto] = useState<any>();
  const [grupos, setGrupos] = useState<any[]>([]);

  const handleSubmitProduto = useCallback(
    async (data: any) => {
      if (!data.produto_sugestao) {
        addToast({
          type: 'error',
          title: 'Opss.',
          description: 'Selecione um produto.',
        });

        return;
      }

      try {
        await api.post('/parametro', {
          empresa_id: user.empresa_id,
          descricao: 'produto_sugestao',
          role: JSON.stringify({
            id: data.produto_sugestao,
          }),
        });

        addToast({
          type: 'success',
          title: 'Tudo certo.',
          description: 'Parâmetro salvo com sucesso.',
        });
      } catch (err) {
        const { message } = (err as any).response.data;

        addToast({
          type: 'error',
          title: 'Algo deu errado.',
          description: message,
        });
      }
    },
    [user, addToast],
  );

  const handleSelectAllGrupos = useCallback((value: boolean) => {
    setGrupos((oldState) =>
      oldState.map((grupo) => ({
        ...grupo,
        checked: value,
      })),
    );
  }, []);

  const handleEditGrupo = useCallback((id: number, value: boolean) => {
    setGrupos((oldState) =>
      oldState.map((grupo) => {
        if (grupo.id === id) {
          return {
            ...grupo,
            checked: value,
          };
        }

        return grupo;
      }),
    );
  }, []);

  const handleSubmitGrupos = useCallback(async () => {
    try {
      await api.post('/parametro', {
        empresa_id: user.empresa_id,
        descricao: 'grupos_sugestao',
        role: JSON.stringify({
          grupos: grupos
            .filter((grupo) => grupo.checked)
            .map((grupo) => grupo.id),
        }),
      });

      addToast({
        type: 'success',
        title: 'Tudo certo.',
        description: 'Parâmetro salvo com sucesso.',
      });
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        type: 'error',
        title: 'Algo deu errado.',
        description: message,
      });
    }
  }, [grupos, user, addToast]);

  useEffect(() => {
    async function loadProducts(): Promise<void> {
      try {
        const response = await api.get(`/produto/empresa/${user.empresa_id}`);

        const parametro = await api.get('/parametro', {
          params: {
            descricao: 'produto_sugestao',
            empresa_id: user.empresa_id,
          },
        });

        if (parametro.data) {
          const { id } = JSON.parse(parametro.data.role);

          setSelectedProduto({
            id,
            value: id,
            label: response.data.find((produto: any) => produto.id === id)
              ?.descricao,
          });
        }

        setProdutos(
          response.data
            .filter((produto: any) => produto.tipo === 1)
            .map((produto: any) => ({
              id: produto.id,
              label: produto.descricao,
              value: produto.id,
            })),
        );
      } catch (err) {
        addToast({
          title: 'Algo deu errado',
          type: 'error',
          description:
            (err as any).response?.data?.message || 'Tente novamente.',
        });
      }
    }

    async function loadGroups(): Promise<void> {
      try {
        const response = await api.get('/grupo');

        const parametro = await api.get('/parametro', {
          params: {
            descricao: 'grupos_sugestao',
            empresa_id: user.empresa_id,
          },
        });

        if (parametro.data) {
          const { grupos: storedGrupos } = JSON.parse(parametro.data.role);

          setGrupos(
            response.data.map((grupo: any) => ({
              id: grupo.id,
              label: grupo.descricao,
              checked: storedGrupos.includes(grupo.id),
            })),
          );

          return;
        }

        setGrupos(
          response.data.map((grupo: any) => ({
            id: grupo.id,
            label: grupo.descricao,
            checked: false,
          })),
        );
      } catch (err) {
        addToast({
          title: 'Algo deu errado',
          type: 'error',
          description:
            (err as any).response?.data?.message || 'Tente novamente.',
        });
      }
    }

    loadProducts();
    loadGroups();
  }, [user.empresa_id, addToast]);

  return (
    <Wrapper style={{ paddingLeft: 50, paddingRight: 50 }}>
      <Grid container spacing={3} style={{ marginTop: 30 }}>
        <Grid item xs={12}>
          <PageTitle>Produto sugestivo na finalização</PageTitle>
          <InformativeSpan>
            Ao Finalizar o Pedido a aplicação mostrará a sugestão do produto
            especifico. Muito usado em restaurantes para tornar mais dinâmico a
            escolha dos complementos grátis e promocionais.
          </InformativeSpan>
          <Grid
            container
            style={{ display: 'block', paddingBottom: 60, paddingTop: 30 }}
          >
            <h2 style={{ marginBottom: -15 }}>Produto sugestivo</h2>
            <InformativeSpan>
              Selecione o produto que deverá ser sugerido no pedido
            </InformativeSpan>
            <Form onSubmit={handleSubmitProduto}>
              <Select
                name="produto_sugestao"
                placeholder="Sugestão"
                options={produtos}
                value={selectedProduto}
                onChange={(value) => setSelectedProduto(value)}
              />
              <Grid container style={{ paddingTop: 20 }}>
                <Grid item xs={12}>
                  <Grid container justify="center">
                    <Grid item lg={4} md={6} xs={12}>
                      <ButtonStyled type="submit">Salvar</ButtonStyled>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          </Grid>
          <Grid container style={{ display: 'block' }}>
            <h2 style={{ marginBottom: -15 }}>Grupos sugestivos</h2>
            <InformativeSpan>
              Selecione os grupos que deverão ter o produto sugerido no pedido.
            </InformativeSpan>
            <label
              htmlFor="all"
              style={{
                marginRight: 10,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <input
                type="checkbox"
                name="all"
                id="all"
                checked={grupos.every((grupo: any) => grupo.checked)}
                onChange={(event) =>
                  handleSelectAllGrupos(event.target.checked)
                }
                style={{ marginRight: 5 }}
              />
              Todos
            </label>
            {grupos.map((grupo) => (
              <label
                key={grupo.id}
                htmlFor={`${grupo.label}_${grupo.id}`}
                style={{
                  marginRight: 10,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <input
                  type="checkbox"
                  name={`${grupo.label}_${grupo.id}`}
                  id={`${grupo.label}_${grupo.id}`}
                  style={{ marginRight: 5 }}
                  checked={grupo.checked}
                  onChange={(event) =>
                    handleEditGrupo(grupo.id, event.target.checked)
                  }
                />
                {grupo.label}
              </label>
            ))}
            <Grid container>
              <Grid item xs={12}>
                <Grid container justify="center">
                  <Grid item lg={4} md={6} xs={12}>
                    <ButtonStyled type="button" onClick={handleSubmitGrupos}>
                      Salvar
                    </ButtonStyled>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Wrapper>
  );
};

export default SuggestiveProduct;
